/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dialog } from '@coursera/cds-core';

import type { AIMessageDraft } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import EmailPreviewBody from 'bundles/enterprise-admin-messaging-v2/components/common/EmailPreviewBody';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

// TODO: source From/To from the org profile + the resolved learner list.
// Email addresses aren't translated; the org name is wrapped in _t() at the
// call site (render time) per Coursera translation standards.
const STUB_FROM_EMAIL = 'noreply@example.com';
const STUB_TO_ADDRESS = 'learner@example.com';

type Props = {
  draft: AIMessageDraft;
  /** Send action. Omit for a read-only preview (no actions bar). */
  onSend?: () => void;
  /** Back action. Omit for a read-only preview (no actions bar). */
  onBack?: () => void;
};

const styles = {
  actions: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
    width: 100%;
  `,
};

/**
 * Preview-and-send sub-screen. Renders the compiled email inside a sandboxed
 * iframe via {@link EmailPreviewBody}. Send transitions to the Sent
 * confirmation; Back returns to Review with the draft preserved. With neither
 * handler the preview is read-only (no actions bar) — e.g. the "See preview"
 * link on the resend success toast.
 */
const PreviewEmailScreen = ({ draft, onSend, onBack }: Props) => {
  return (
    <React.Fragment>
      <Dialog.Content>
        <EmailPreviewBody
          draft={draft}
          fromName={_t('Your Organization')}
          fromEmail={STUB_FROM_EMAIL}
          toAddress={STUB_TO_ADDRESS}
          dataTestIdPrefix="ai-message-modal-preview"
        />
      </Dialog.Content>
      {onSend || onBack ? (
        <Dialog.Actions>
          <div css={styles.actions}>
            {onSend ? (
              <Button variant="primary" onClick={onSend} data-testid="ai-message-modal-preview-send">
                {_t('Send')}
              </Button>
            ) : null}
            {onBack ? (
              <Button variant="ghost" onClick={onBack} data-testid="ai-message-modal-preview-back">
                {_t('Back')}
              </Button>
            ) : null}
          </div>
        </Dialog.Actions>
      ) : null}
    </React.Fragment>
  );
};

export default PreviewEmailScreen;
